const SCORE_TYPES = [
  {
    num: '01',
    icon: 'ri-boxing-line',
    label: 'BATTLE',
    title: '戰鬥積分',
    desc: '體能、智力、料理對抗的硬實力表現，每場勝利都是對手的眼淚。',
    color: '#e05555',
  },
  {
    num: '02',
    icon: 'ri-heart-2-line',
    label: 'CHEMISTRY',
    title: '默契加分',
    desc: '對彼此了解程度、情感共鳴、任務湯品，用心就能得分。',
    color: '#d4a843',
  },
];

function HudSectionHeader({ label, title }: { label: string; title: string }) {
  return (
    <div className="text-center mb-8 sm:mb-14">
      <div className="flex items-center justify-center gap-3 mb-4">
        <div className="h-px w-16 sm:w-24" style={{ background: 'linear-gradient(90deg, transparent, #d4a84340)' }} />
        <div className="flex items-center gap-2 px-4 py-1.5 rounded-full" style={{ background: '#d4a84312', border: '1px solid #d4a84335' }}>
          <i className="ri-trophy-line text-[#d4a843] text-xs" />
          <span className="text-[#d4a843] text-[10px] tracking-[0.4em] uppercase font-black font-cinzel whitespace-nowrap">{label}</span>
        </div>
        <div className="h-px w-16 sm:w-24" style={{ background: 'linear-gradient(90deg, #d4a84340, transparent)' }} />
      </div>
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mt-2 mb-4 font-serif">{title}</h2>
      <div className="flex items-center justify-center gap-3">
        <div className="h-px w-8 sm:w-14" style={{ background: '#d4a84340' }} />
        <div className="w-1.5 h-1.5 rounded-full" style={{ background: '#d4a843', boxShadow: '0 0 6px #d4a843' }} />
        <div className="h-px w-8 sm:w-14" style={{ background: '#d4a84340' }} />
      </div>
    </div>
  );
}

export default function ScoreSection() {
  return (
    <section id="score" className="relative overflow-hidden py-14 sm:py-20 px-5 sm:px-6 border-t border-[#16172a]" style={{ background: '#06060f' }}>

      {/* ── Decorative score background ── */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle, rgba(212,168,67,0.045) 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,168,67,0.35), transparent)' }} />
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,168,67,0.15), transparent)' }} />
        <div className="absolute top-5 left-5 w-7 h-7 border-l-2 border-t-2 border-[#d4a843]/30" />
        <div className="absolute top-5 right-5 w-7 h-7 border-r-2 border-t-2 border-[#d4a843]/30" />
        <div className="absolute bottom-5 left-5 w-7 h-7 border-l border-b border-[#d4a843]/18" />
        <div className="absolute bottom-5 right-5 w-7 h-7 border-r border-b border-[#d4a843]/18" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-72 h-24 opacity-20 blur-3xl" style={{ background: 'radial-gradient(ellipse, rgba(212,168,67,0.6) 0%, transparent 70%)' }} />
      </div>

      <div className="max-w-5xl mx-auto">

        <HudSectionHeader label="Scoring & Rewards" title="積分與獎勵機制" />

        {/* Score type ability cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-8 sm:mb-12">
          {SCORE_TYPES.map(s => (
            <div
              key={s.num}
              className="relative group rounded-lg p-5 sm:p-6 border hover:border-[#252640] transition-all duration-300 overflow-hidden"
              style={{ background: '#0c0d18', borderColor: '#1e2035' }}
            >
              {/* Corner brackets */}
              <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2" style={{ borderColor: `${s.color}35` }} />
              <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2" style={{ borderColor: `${s.color}35` }} />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2" style={{ borderColor: `${s.color}35` }} />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2" style={{ borderColor: `${s.color}35` }} />
              <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: `radial-gradient(ellipse at 30% 30%, ${s.color}07 0%, transparent 65%)` }} />
              <div className="absolute right-4 bottom-3 text-6xl font-black leading-none select-none font-cinzel" style={{ color: `${s.color}08` }}>{s.num}</div>
              <div className="w-12 h-12 flex items-center justify-center rounded-full mb-4 relative z-10" style={{ background: `${s.color}12`, border: `1px solid ${s.color}35` }}>
                <i className={`${s.icon} text-2xl`} style={{ color: s.color, filter: `drop-shadow(0 0 6px ${s.color}60)` }} />
              </div>
              <div className="relative z-10">
                <span className="text-[10px] tracking-[0.4em] uppercase font-bold mb-1 block font-cinzel" style={{ color: `${s.color}55` }}>{s.label}</span>
                <h3 className="font-bold text-base mb-2" style={{ color: s.color }}>{s.title}</h3>
                <p className="text-[#4a5068] text-sm leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Final Rewards header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="h-px flex-1 bg-[#16172a]" />
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rotate-45 bg-[#d4a843]/40" />
            <span className="text-[#3a3a5a] text-[10px] tracking-[0.5em] uppercase font-cinzel whitespace-nowrap">Final Rewards</span>
            <div className="w-1.5 h-1.5 rotate-45 bg-[#d4a843]/40" />
          </div>
          <div className="h-px flex-1 bg-[#16172a]" />
        </div>

        {/* Champion card */}
        <div
          className="relative rounded-xl overflow-hidden border border-[#d4a843]/30 mb-4"
          style={{ background: 'linear-gradient(135deg, #13100a 0%, #0c0d18 55%, #0e0c14 100%)' }}
        >
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#d4a843] to-transparent" />
          <div className="absolute top-0 left-0 w-5 h-5 border-l-2 border-t-2 border-[#d4a843]/70" />
          <div className="absolute top-0 right-0 w-5 h-5 border-r-2 border-t-2 border-[#d4a843]/70" />
          <div className="absolute bottom-0 left-0 w-5 h-5 border-l-2 border-b-2 border-[#d4a843]/70" />
          <div className="absolute bottom-0 right-0 w-5 h-5 border-r-2 border-b-2 border-[#d4a843]/70" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(212,168,67,0.12) 0%, transparent 60%)' }} />
          <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-6">
            <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center rounded-full" style={{ background: 'radial-gradient(circle, #181200 0%, #0c0d18 70%)', border: '2px solid rgba(212,168,67,0.5)', boxShadow: '0 0 24px rgba(212,168,67,0.25)' }}>
              <i className="ri-trophy-fill text-3xl sm:text-4xl" style={{ color: '#d4a843', filter: 'drop-shadow(0 0 10px rgba(212,168,67,0.7))' }} />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <span className="inline-block text-[10px] font-bold px-3 py-1 rounded-full mb-3 tracking-[0.4em] font-cinzel" style={{ background: '#d4a843', color: '#06060f' }}>CHAMPION</span>
              <div className="text-3xl sm:text-4xl font-black mb-1 tabular-nums font-orbitron" style={{ color: '#d4a843', textShadow: '0 0 20px rgba(212,168,67,0.5)' }}>NT$ 2,000</div>
              <div className="text-[#d4a843]/50 text-[10px] tracking-[0.4em] uppercase mb-3 font-cinzel">圓夢獎勵金</div>
              <p className="text-[#4a5068] text-sm leading-relaxed max-w-sm">積分最高隊伍奪得！留作下一次重逢的圓夢基金，夢想由你們親手實現。</p>
            </div>
          </div>
        </div>

        {/* Runner-up card */}
        <div className="relative rounded-xl overflow-hidden border border-[#1e2035]" style={{ background: '#0c0d18' }}>
          <div className="absolute top-0 left-0 w-4 h-4 border-l border-t border-[#555]/30" />
          <div className="absolute top-0 right-0 w-4 h-4 border-r border-t border-[#555]/30" />
          <div className="absolute bottom-0 left-0 w-4 h-4 border-l border-b border-[#555]/30" />
          <div className="absolute bottom-0 right-0 w-4 h-4 border-r border-b border-[#555]/30" />
          <div className="p-5 sm:p-6 flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-5">
            <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center rounded-full" style={{ background: '#12131e', border: '1px solid #252640' }}>
              <i className="ri-medal-fill text-2xl text-[#888]" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <span className="inline-block text-[10px] font-bold px-3 py-1 rounded-full mb-2 tracking-[0.4em] font-cinzel bg-[#16172a] text-[#555]">RUNNER-UP</span>
              <h3 className="text-white text-lg font-black mb-1 font-serif">榮譽與情誼</h3>
              <p className="text-[#4a5068] text-sm leading-relaxed max-w-sm">雖敗猶榮，真正的勝利是這段共同走過的旅程。但下次——再來奪回它。</p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
